import React, { useState, useContext } from 'react';
import {
  Box,
  VStack,
  Text,
  HStack,
  Button,
  Image,
  useBreakpointValue,
} from '@chakra-ui/react';
import { Link, useLocation, Link as RouterLink, useNavigate } from 'react-router-dom';
import { 
  FaHome, 
  FaBars, 
  FaChartLine, 
  FaBuilding,
  FaClipboardList,
  FaExchangeAlt,
  FaTrophy,
  FaChartBar,
  FaCalendarAlt,
  FaSearch,
  FaEnvelope,
  FaUserFriends,
  FaFileContract,
  FaUser,
  FaSignOutAlt,
  FaInfoCircle,
  FaQuestionCircle,
  FaDollarSign
} from 'react-icons/fa';
import { FiChevronDown, FiChevronUp, FiChevronLeft } from 'react-icons/fi';
import dominusLogo from '../assets/dominus_logo_transparent_white.png';
import Terms from './Terms';
import { useTerms } from '../hooks/useTerms';
import { useUserContext } from '../contexts/UserContext';
import { isAdmin } from '../hooks/useUser';
import { signOut } from '../utils/signOut';

interface NavItemProps {
  icon: React.ReactElement;
  label: string;
  to: string;
  isCollapsed: boolean;
}

interface NavSectionProps {
  icon: React.ReactElement;
  label: string;
  children: React.ReactNode;
  isCollapsed: boolean;
  navigateTo?: string;
  onMyTeamClick?: () => void;
}

interface NavigationMenuProps {
  onCollapse: (isCollapsed: boolean) => void;
  isMobileMenuOpen: boolean;
  onMobileMenuToggle: (isOpen: boolean) => void;
}

const MobileNavContext = React.createContext<{ closeMenu?: () => void }>({});

const NavItem = ({ icon, label, to, isCollapsed }: NavItemProps) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { closeMenu } = useContext(MobileNavContext);
  const isActive = location.pathname === to;

  const handleRosterClick = () => {
    if (to.startsWith('/roster') && window.location.pathname === '/roster') {
      let tabParam = null;
      if (to.includes('?')) {
        const urlParams = new URLSearchParams(to.split('?')[1]);
        tabParam = urlParams.get('tab');
      }

      window.dispatchEvent(new CustomEvent('switchToMyTeam', {
        detail: { tab: tabParam }
      }));
      return true;
    }

    return false;
  };

  const handleLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();
    const handled = handleRosterClick();
    if (!handled && to !== '#') {
      navigate(to);
    }
    closeMenu?.();
  };

  return (
    <Box
      as="button"
      type="button"
      w="full"
      bg="transparent"
      border="none"
      cursor="pointer"
      p={0}
      textAlign="left"
      onClick={handleLinkClick}
    >
      <HStack
        w="full"
        p={2}
        borderRadius="md"
        bg={isActive ? 'whiteAlpha.200' : 'transparent'}
        _hover={{ bg: 'whiteAlpha.100' }}
        transition="all 0.2s"
      >
        <Box color="white">{icon}</Box>
        {!isCollapsed && (
          <Text color="white" fontSize="sm" textAlign="left">
            {label}
          </Text>
        )}
      </HStack>
    </Box>
  );
};

const NavSection = ({ icon, label, children, isCollapsed, navigateTo, onMyTeamClick }: NavSectionProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();
  
  const toggleOpen = () => {
    const wasClosed = !isOpen;
    setIsOpen(!isOpen);
    
    // If navigateTo is provided, navigate there (whether opening or already open)
    if (navigateTo) {
      // Always navigate to the target route
      navigate(navigateTo);
      // For "My Team", also request switching to my team
      if (label === "My Team") {
        window.dispatchEvent(new CustomEvent('switchToMyTeam'));
        if (onMyTeamClick) onMyTeamClick();
      }
    }
  };

  if (isCollapsed) {
    return (
      <Box>
        <HStack
          w="full"
          p={2}
          borderRadius="md"
          _hover={{ bg: 'whiteAlpha.100' }}
          transition="all 0.2s"
          onClick={toggleOpen}
          cursor="pointer"
        >
          <Box color="white">{icon}</Box>
        </HStack>
      </Box>
    );
  }

  // Check if there are any visible children (not commented out)
  const hasVisibleChildren = React.Children.count(children) > 0;

  return (
    <Box>
      <HStack
        p={2}
        _hover={{ bg: 'whiteAlpha.100' }}
        borderRadius="md"
        cursor="pointer"
        onClick={toggleOpen}
      >
        <Box color="white">{icon}</Box>
        <Text color="white" fontSize="sm">{label}</Text>
        {hasVisibleChildren && (
          <Box color="white" ml="auto">
            {isOpen ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
          </Box>
        )}
      </HStack>
      {hasVisibleChildren && (
        <Box display={isOpen ? 'block' : 'none'}>
          <VStack align="stretch" pl={4} spacing={0.5}>
            {children}
          </VStack>
        </Box>
      )}
    </Box>
  );
};

// Lookup table for roster page tab parameters
const ROSTER_TAB_PARAMS: Record<string, string> = {
  'lineup': 'lineup',
  'depth': 'depthchart', 
  'pitchers': 'pitchers',
  'office': 'frontoffice',
  'upgrade': 'upgrade'
};

const NavigationMenu = ({ onCollapse, isMobileMenuOpen, onMobileMenuToggle }: NavigationMenuProps) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const bgColor = 'black';
  const isMobile = useBreakpointValue({ base: true, md: false }) ?? false;
  const menuCollapsed = isMobile ? false : isCollapsed;
  const { myTeam } = useUserContext();
  const isAdminUser = isAdmin();

  const handleCollapse = () => {
    if (isMobile) {
      onMobileMenuToggle(!isMobileMenuOpen);
      return;
    }

    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    onCollapse(newCollapsedState);
  };

  const collapseIcon = isMobile
    ? <FiChevronLeft size="16px" />
    : (isCollapsed ? <FaBars size="16px" /> : <FiChevronLeft size="16px" />);
  const {
    isOpen: termsOpen,
    documentPath,
    title,
    displayMode,
    showAcceptDecline,
    acceptText,
    declineText,
    onAccept,
    onDecline,
    openTerms,
    closeTerms,
  } = useTerms();
  const [privacyOpen, setPrivacyOpen] = useState(false);

  const shouldRenderContent = !isMobile || isMobileMenuOpen;
  const widthValue = isMobile ? (isMobileMenuOpen ? '200px' : '0px') : (isCollapsed ? '60px' : '200px');
  const closeMobileMenu = () => {
    if (isMobile && isMobileMenuOpen) {
      onMobileMenuToggle(false);
    }
  };

  return (
    <MobileNavContext.Provider value={{ closeMenu: closeMobileMenu }}>
      <Box
        position="fixed"
        left={0}
        top={0}
        h="100vh"
        bg={bgColor}
        borderRight="2px solid"
        borderColor="transparent"
        _after={{
          content: '""',
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: '2px',
          background: 'linear-gradient(to bottom, rgba(0,46,11,0.7), rgba(0,0,0,0.7))',
          pointerEvents: 'none'
        }}
        transition="width 0.3s"
        zIndex={1000}
        width={widthValue}
        minWidth={widthValue}
        overflowY="auto"
        overflowX="hidden"
        pointerEvents={isMobile && !isMobileMenuOpen ? 'none' : 'auto'}
        aria-hidden={isMobile && !isMobileMenuOpen}
        maxHeight="100vh"
      >
      <VStack
        h="full"
        align="stretch"
        p={shouldRenderContent ? 2 : 0}
        display={shouldRenderContent ? 'flex' : 'none'}
      >
        {/* Logo and Collapse Button */}
        <HStack justify="space-between" align="center" mb={2} w="100%">
          {!menuCollapsed && (
            <Box flexShrink={0} maxW="120px" ml={1}>
              <Link to="/dashboard">
                <Image 
                  src={dominusLogo} 
                  alt="Dominus Logo" 
                  height="24px" 
                  maxW="120px" 
                  objectFit="contain"
                  _hover={{ opacity: 0.8 }}
                  transition="opacity 0.2s"
                />
              </Link>
            </Box>
          )}
          <Box flexShrink={0} mr={1}>
            <Button
              aria-label="Toggle menu"
              variant="ghost"
              color="white"
              size="sm"
              w="30px"
              h="30px"
              onClick={handleCollapse}
              bg="whiteAlpha.100"
              _hover={{ bg: 'whiteAlpha.200' }}
              _active={{ boxShadow: 'none', outline: 'none' }}
              _focus={{ boxShadow: 'none', outline: 'none' }}
              _before={{
                content: '""',
                position: 'absolute',
                inset: 0,
                bg: 'blackAlpha.200',
                borderRadius: 'md',
                zIndex: -1
              }}
            >
              <Box
                as="span"
                color="white"
                filter="drop-shadow(0 0 1px rgba(0,0,0,0.5))"
              >
                {collapseIcon}
              </Box>
            </Button>
          </Box>
        </HStack>

        {/* Dashboard */}
        <NavItem
          icon={<FaHome />}
          label="Dashboard"
          to="/dashboard"
          isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
        />

        {/* My Team Section */}
        {myTeam && (
          <NavSection
            icon={<FaBuilding />}
            label="My Team"
            navigateTo="/roster"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            onMyTeamClick={() => {
              // If we're on the roster page, trigger a team switch to my team
              if (window.location.pathname === '/roster') {
                // Dispatch a custom event to switch to my team
                window.dispatchEvent(new CustomEvent('switchToMyTeam'));
              }
            }}
          >
            <NavItem
              icon={<FaUserFriends />}
              label="Roster"
              to="/roster"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaClipboardList />}
              label="Lineups"
              to="/roster?tab=lineup"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaClipboardList />}
              label="Depth Chart"
              to="/roster?tab=depthchart"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaExchangeAlt />}
              label="My Trades"
              to="/my-trades"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
              <NavItem
                icon={<FaDollarSign />}
                label="Store"
                to="/store"
                isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
              />
              <NavItem
                icon={<FaUserFriends />}
                label="Join a League"
                to="/join-league"
                isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
              />
            {/* <NavItem
              icon={<FaFileContract />}
              label="Team Admin"
              to="/team-administration"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            /> */}
          </NavSection>
        )}

        {/* Scores & Stats Section */}
        {myTeam && (
          <NavSection
            icon={<FaChartLine />}
            label="Scores & Stats"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          >
            <NavItem
              icon={<FaClipboardList />}
              label="Standings"
              to="/standings"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaChartBar />}
              label="Scoreboard"
              to="/scoreboard"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaChartLine />}
              label="League Stats"
              to="/league-stats"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaCalendarAlt />}
              label="Schedule"
              to="/league-schedule"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
          </NavSection>
        )}

        {/* Transactions Section */}
        {myTeam && (
          <NavSection
            icon={<FaExchangeAlt />}
            label="Transactions"
            navigateTo="/waiver-wire"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          >
            <NavItem
              icon={<FaClipboardList />}
              label="The Wire"
              to="/transactions"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaExchangeAlt />}
              label="Trades"
              to="/trades"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaDollarSign />}
              label="Purchases"
              to="/purchases"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            {/* <NavItem
              icon={<FaDollarSign />}
              label="Signed Free Agents"
              to="/free-agents"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <NavItem
              icon={<FaClipboardList />}
              label="Claimed on Waivers"
              to="/waivers"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            /> */}
          </NavSection>
        )}

        {/* Research Section */}
        <NavSection
          icon={<FaSearch />}
          label="Research"
          isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
        >
          <NavItem
            icon={<FaClipboardList />} // Teams icon
            label="Teams"
            to="/rosters/all"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          <NavItem
            icon={<FaUserFriends />} // Players icon
            label="Players"
            to="/research"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          {myTeam && (
            <>
              <NavItem
                icon={<FaUserFriends />} // Free Agents icon
                label="Waiver Wire"
                to="/waiver-wire"
                isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
              />
              <NavItem
                icon={<FaDollarSign />}
                label="Free Agents"
                to="/free-agents"
                isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
              />
            </>
          )}
        </NavSection>

        {/* Messages */}
        <NavItem
          icon={<FaEnvelope />}
          label="Messages"
          to="/messages"
          isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
        />

        {/* About Section (Formally How It Works) */}
        <NavSection
          icon={<FaQuestionCircle />} // changed icon
          label="About"
          navigateTo="/about/how-it-works"
          isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
        >
          <NavItem
            icon={<FaInfoCircle />}
            label="How It Works"
            to="/about/how-it-works"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          <NavItem
            icon={<FaDollarSign />}
            label="Entry Fees"
            to="/about/entry-fees"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          <NavItem
            icon={<FaTrophy />}
            label="Prize Pool"
            to="/about/prize-pool"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          <NavItem
            icon={<FaExchangeAlt />}
            label="Key League Dates"
            to="/about/key-league-dates" 
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
          <NavItem
            icon={<FaClipboardList />}
            label="Rules"
            to="/about/rules"
            isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
          />
        </NavSection>

        {/* User Section */}
        {!menuCollapsed && (
          <Box p={2} mt="auto" display="flex" flexDirection="column" alignItems="stretch">
            <NavItem
              icon={<FaUser />}
              label="My Profile"
              to="/my-profile"
              isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
            />
            <Box
              as="div"
              onClick={() => signOut()}
              style={{ cursor: 'pointer' }}
            >
              <NavItem
                icon={<FaSignOutAlt />}
                label="Sign Out"
                to="#"
                isCollapsed={menuCollapsed} onNavigate={closeMobileMenu}
              />
            </Box>
            <Box as="hr" borderColor="whiteAlpha.200" borderWidth="1px 0 0 0" my={2} />
            <RouterLink
              to="/contact"
              style={{
                color: 'white',
                textDecoration: 'none',
                fontSize: '0.8em',
                marginTop: '1em',
                display: 'block',
                textAlign: 'left',
                transition: 'color 0.2s, text-decoration 0.2s',
              }}
              onMouseOver={e => {
                e.currentTarget.style.color = '#22a000';
                e.currentTarget.style.textDecoration = 'underline';
              }}
              onMouseOut={e => {
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.textDecoration = 'none';
              }}
            >
              Contact Us
            </RouterLink>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.7em', marginTop: '0.5em' }}>
              <button
                type="button"
                style={{
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.8em',
                  display: 'inline',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'color 0.2s, text-decoration 0.2s',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.color = '#22a000';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.textDecoration = 'none';
                }}
                onClick={() => openTerms('terms-and-conditions.html', 'Terms & Conditions', { displayMode: 'popup' })}
              >
                Terms
              </button>
              <span style={{ color: 'white', fontSize: '0.8em' }}>|</span>
              <button
                type="button"
                style={{
                  color: 'white',
                  textDecoration: 'none',
                  fontSize: '0.8em',
                  display: 'inline',
                  textAlign: 'left',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  transition: 'color 0.2s, text-decoration 0.2s',
                }}
                onMouseOver={e => {
                  e.currentTarget.style.color = '#22a000';
                  e.currentTarget.style.textDecoration = 'underline';
                }}
                onMouseOut={e => {
                  e.currentTarget.style.color = 'white';
                  e.currentTarget.style.textDecoration = 'none';
                }}
                onClick={() => setPrivacyOpen(true)}
              >
                Privacy
              </button>
            </div>
            {termsOpen && (
              <Terms
                documentPath={documentPath}
                title={title}
                isOpen={termsOpen}
                onClose={closeTerms}
                displayMode={displayMode}
                showAcceptDecline={showAcceptDecline}
                acceptText={acceptText}
                declineText={declineText}
                onAccept={onAccept}
                onDecline={onDecline}
              />
            )}
            {privacyOpen && (
              <Terms
                documentPath="privacy-policy.html"
                title="Privacy Policy"
                isOpen={privacyOpen}
                onClose={() => setPrivacyOpen(false)}
                displayMode="popup"
              />
            )}
          </Box>
        )}
      </VStack>
    </Box>
  </MobileNavContext.Provider>
  );
};

export default NavigationMenu;